/**
 * Phase 16F header bell (REQ-NTF-01): badge = my New count, opens the notification panel.
 * Renders nothing outside the staff dashboard's NotificationProvider or without
 * notifications:self:read (guardians, ops console, offline screens never mount it).
 */
import React, { useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import NotificationPanel from './NotificationPanel';

export const PANEL_ID = 'notification-panel';

export const badgeText = (count: number): string => (count > 99 ? '99+' : String(count));

const NotificationBell: React.FC = () => {
  const ctx = useNotifications();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelOpen = ctx?.panelOpen ?? false;
  const setPanelOpen = ctx?.setPanelOpen;

  // Close on outside click and Escape (focus returns to the bell)
  useEffect(() => {
    if (!panelOpen || !setPanelOpen) return undefined;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node | null;
      // Dialogs opened from the panel render outside it; only close for clicks on the page itself
      if (target instanceof Element && target.closest('[role="dialog"]')) return;
      if (wrapperRef.current && target && !wrapperRef.current.contains(target)) setPanelOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.querySelector('[data-notification-dialog="open"]')) {
        setPanelOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [panelOpen, setPanelOpen]);

  if (!ctx || !ctx.enabled) return null;
  const count = ctx.count ?? 0;
  const label = count > 0 ? `Notifications, ${count} new` : 'Notifications';

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => ctx.setPanelOpen(!ctx.panelOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={ctx.panelOpen}
        aria-controls={PANEL_ID}
        data-testid="notification-bell"
      >
        <Bell size={18} aria-hidden="true" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-[18px] text-center"
            aria-hidden="true"
            data-testid="notification-badge"
          >
            {badgeText(count)}
          </span>
        )}
      </button>
      {/* Screen readers hear the count when it changes */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {ctx.count === null ? '' : `${count} new ${count === 1 ? 'notification' : 'notifications'}`}
      </span>
      {ctx.panelOpen && <NotificationPanel id={PANEL_ID} onClose={() => { ctx.setPanelOpen(false); buttonRef.current?.focus(); }} />}
    </div>
  );
};

export default NotificationBell;
